/**
 * BuilderBuddi brand mark — roofline + ruler concept.
 *
 * Geometry (viewBox 0 0 24 24):
 *   - Two roof slopes meeting at a peak (12, 4)        → building / structure
 *   - Horizontal baseline connecting the eaves (y=12)  → ruler / planning edge
 *   - Three measurement ticks below the baseline       → precision / measurement
 *   - Burnt orange accent circle at the peak           → brand identity
 *
 * All strokes use hardcoded presentation attributes (not CSS/currentColor) so
 * the mark renders correctly in iOS Safari PWA mode where CSS cascade is fragile.
 */

interface BuilderBuddiMarkProps {
  /** Rendered pixel size (width = height). Default 22. */
  size?: number
  className?: string
}

/** Light-surface variant — navy strokes, burnt orange peak. */
export function BuilderBuddiMark({ size = 22, className }: BuilderBuddiMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      {/* Roof slopes */}
      <line x1="2"  y1="12" x2="12" y2="4"    stroke="#1e3a5f" strokeWidth="2"    strokeLinecap="round" />
      <line x1="12" y1="4"  x2="22" y2="12"   stroke="#1e3a5f" strokeWidth="2"    strokeLinecap="round" />
      {/* Eave baseline */}
      <line x1="2"  y1="12" x2="22" y2="12"   stroke="#1e3a5f" strokeWidth="2"    strokeLinecap="round" />
      {/* Measurement ticks */}
      <line x1="6"  y1="12" x2="6"  y2="17.5" stroke="#1e3a5f" strokeWidth="1.75" strokeLinecap="round" />
      <line x1="12" y1="12" x2="12" y2="16"   stroke="#1e3a5f" strokeWidth="1.75" strokeLinecap="round" />
      <line x1="18" y1="12" x2="18" y2="17.5" stroke="#1e3a5f" strokeWidth="1.75" strokeLinecap="round" />
      {/* Peak accent — burnt orange identity dot */}
      <circle cx="12" cy="4" r="2.25" fill="#F0853A" />
    </svg>
  )
}

/** Dark-surface / sidebar variant — white strokes, lighter orange peak. */
export function BuilderBuddiMarkDark({ size = 22, className }: BuilderBuddiMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <line x1="2"  y1="12" x2="12" y2="4"    stroke="white" strokeWidth="2"    strokeLinecap="round" />
      <line x1="12" y1="4"  x2="22" y2="12"   stroke="white" strokeWidth="2"    strokeLinecap="round" />
      <line x1="2"  y1="12" x2="22" y2="12"   stroke="white" strokeWidth="2"    strokeLinecap="round" />
      <line x1="6"  y1="12" x2="6"  y2="17.5" stroke="white" strokeWidth="1.75" strokeLinecap="round" />
      <line x1="12" y1="12" x2="12" y2="16"   stroke="white" strokeWidth="1.75" strokeLinecap="round" />
      <line x1="18" y1="12" x2="18" y2="17.5" stroke="white" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="12" cy="4" r="2.25" fill="#f0883e" />
    </svg>
  )
}

/** Splash screen variant — white strokes with animated draw-in support. */
export function BuilderBuddiMarkSplash({ size = 96 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Left slope */}
      <line
        x1="2" y1="12" x2="12" y2="4"
        stroke="white" strokeWidth="2" strokeLinecap="round"
        strokeDasharray="14" strokeDashoffset="14"
        style={{ animation: 'bb-draw 0.45s ease forwards 0.05s' }}
      />
      {/* Right slope */}
      <line
        x1="12" y1="4" x2="22" y2="12"
        stroke="white" strokeWidth="2" strokeLinecap="round"
        strokeDasharray="14" strokeDashoffset="14"
        style={{ animation: 'bb-draw 0.45s ease forwards 0.2s' }}
      />
      {/* Eave baseline */}
      <line
        x1="2" y1="12" x2="22" y2="12"
        stroke="white" strokeWidth="2" strokeLinecap="round"
        strokeDasharray="21" strokeDashoffset="21"
        style={{ animation: 'bb-draw 0.5s ease forwards 0.38s' }}
      />
      {/* Peak dot */}
      <circle
        cx="12" cy="4" r="2.25" fill="#f0883e"
        style={{ opacity: 0, animation: 'bb-fade 0.3s ease forwards 0.55s' }}
      />
      {/* Tick left */}
      <line
        x1="6" y1="12" x2="6" y2="17.5"
        stroke="white" strokeWidth="1.75" strokeLinecap="round"
        strokeDasharray="7" strokeDashoffset="7"
        style={{ animation: 'bb-draw 0.25s ease forwards 0.65s' }}
      />
      {/* Tick centre */}
      <line
        x1="12" y1="12" x2="12" y2="16"
        stroke="white" strokeWidth="1.75" strokeLinecap="round"
        strokeDasharray="5" strokeDashoffset="5"
        style={{ animation: 'bb-draw 0.25s ease forwards 0.75s' }}
      />
      {/* Tick right */}
      <line
        x1="18" y1="12" x2="18" y2="17.5"
        stroke="white" strokeWidth="1.75" strokeLinecap="round"
        strokeDasharray="7" strokeDashoffset="7"
        style={{ animation: 'bb-draw 0.25s ease forwards 0.85s' }}
      />
    </svg>
  )
}
